import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import type { ResumeRosterDto, ResumeSnapshotDto, ResumeSource } from '@acs/shared'
import { apiDelete, apiGet, apiPost, apiPut } from '@/lib/api'

/**
 * The resume roster: who was running before the deploy, and the server-side
 * runner that starts them again.
 *
 * One query key for the whole feature. The seed read below fills it; the
 * `resume.state` frames on the SSE stream keep the `run` half current without
 * polling, exactly as `run.state` does for the run map.
 */
export const RESUME_KEY = ['automation', 'resume'] as const

/** What `PUT /roster` answers with. */
export interface SaveRosterResponse {
  roster: ResumeRosterDto
  requested: number
  saved: number
  /** Ids that no longer name a customer. Stripped at the server, reported here. */
  unknown: string[]
}

/**
 * The rosters and the runner.
 *
 * `refetchInterval` is armed ONLY while a resume is in flight, and it is a
 * backstop rather than the transport: progress arrives over SSE, and this is
 * what closes the gap if that stream drops mid-run — the one moment where a
 * stale progress bar would have an operator wondering whether the fleet is
 * coming back. Idle, it polls nothing.
 */
export function useResume(enabled = true): UseQueryResult<ResumeSnapshotDto> {
  return useQuery<ResumeSnapshotDto>({
    queryKey: RESUME_KEY,
    queryFn: ({ signal }) => apiGet<ResumeSnapshotDto>('/api/automation/resume', undefined, signal),
    enabled,
    staleTime: 10_000,
    refetchInterval: (query) => {
      const phase = query.state.data?.run.phase
      return phase === 'running' || phase === 'cancelling' ? 5_000 : false
    },
  })
}

/**
 * Save a roster — either an explicit selection, or everyone running right now.
 *
 * `allRunning` is the one that matters at fleet scale: the table's row
 * selection is per page, so a client-side "select all" can only ever send the
 * page in front of the operator. Asking the server to resolve the set is what
 * makes "save everyone that is running" true across every page.
 */
export function useSaveRoster() {
  const qc = useQueryClient()
  return useMutation<SaveRosterResponse, unknown, { ids: string[] } | { allRunning: true }>({
    mutationFn: (body) => apiPut<SaveRosterResponse>('/api/automation/resume/roster', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: RESUME_KEY })
    },
  })
}

export function useClearRoster() {
  const qc = useQueryClient()
  return useMutation<{ ok: boolean; cleared: boolean }, unknown, ResumeSource>({
    mutationFn: (source) =>
      apiDelete<{ ok: boolean; cleared: boolean }>(
        `/api/automation/resume/roster/${encodeURIComponent(source)}`
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: RESUME_KEY })
    },
  })
}

/**
 * Start a roster. Answers as soon as the work is accepted — the customers are
 * launched by the server, one per stagger, after this resolves.
 *
 * `retry: false` for the same reason every start on this console refuses to
 * retry itself: a second attempt is either refused as already-running or, worse
 * for a request that timed out rather than failed, a second pass over a roster
 * that is already being walked.
 */
export function useStartResume() {
  const qc = useQueryClient()
  return useMutation<{ ok: boolean; total: number }, unknown, ResumeSource>({
    mutationFn: (source) =>
      apiPost<{ ok: boolean; total: number }>('/api/automation/resume/start', { source }),
    retry: false,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: RESUME_KEY })
    },
  })
}

export function useCancelResume() {
  const qc = useQueryClient()
  return useMutation<{ ok: boolean; cancelled: boolean }, unknown, void>({
    mutationFn: () => apiPost<{ ok: boolean; cancelled: boolean }>('/api/automation/resume/stop'),
    retry: false,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: RESUME_KEY })
    },
  })
}
